import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
  Grid,
  IconButton,
} from "@mui/material";
import dayjs from "dayjs";
import { FilePenLine, Check } from "lucide-react";
import { Close } from "@mui/icons-material";
import { getAllTests } from "../../api/TestscoreAPI";

const EXAMS = [
  { id: 9, name: "PTE" },
  { id: 7, name: "TOEFL" },
  { id: 6, name: "Duolingo English Test" },
  { id: 11, name: "IELTS" },
  { id: 2, name: "GMAT" },
  { id: 1, name: "GRE" },
  { id: 10, name: "SAT" },
];

// only allow sending these fields to API
const ALLOWED_FIELDS = [
  "id",
  "sp_user_id",
  "exam_id",
  "exam_date",
  "expiry_date",
  "listening",
  "writing",
  "reading",
  "speaking",
  "literacy",
  "comprehension",
  "conversation",
  "production",
  "analytical_writing",
  "verbal_reasoning",
  "quantitative_apptitude",
  "integrated_reasoning",
  "essay",
  "math",
  "overall",
];

const TestScoreModal = ({
  open,
  onClose,
  onSave,
  editData,
  existingExamIds = [],
}) => {
  const [formData, setFormData] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [testFields, setTestFields] = useState([]);
  const availableExams = editData
    ? EXAMS // in edit mode show the selected exam
    : EXAMS.filter((exam) => !existingExamIds.includes(exam.id));
  useEffect(() => {
    if (editData) {
      setFormData({
        ...editData,
        // fix field mapping
        exam_date: editData.date_of_exam
          ? dayjs(editData.date_of_exam).format("YYYY-MM-DD")
          : "",
        expiry_date: editData.date_of_expiry
          ? dayjs(editData.date_of_expiry).format("YYYY-MM-DD")
          : "",
        overall: editData.over_all ?? "",
      });
    } else {
      setFormData({});
    }
  }, [editData]);

  const getAllTestFields = async () => {
    try {
      const response = await getAllTests();
      setTestFields(response.data || []);
    } catch (err) {
      console.error("Error fetching tests:", err.message);
      throw err;
    }
  };

  useEffect(() => {
    getAllTestFields();
  }, []);

  const showSnackbar = (msg) => {
    setSnackbar({ open: true, message: msg });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "" });
  };

  const handleChange = (field, value) => {
    if (field === "exam_id") {
      // When exam type changes, reset all test score fields
      setFormData({
        exam_id: value,
        exam_date: "",
        expiry_date: "",
      });
      return;
    }
    const testId = formData.exam_id || editData?.exam_id;
    const fields = testFields[testId] || [];

    const allKeys = fields.map((f) => f.key);
    const moduleKeys = allKeys.filter((k) => k !== "overall"); // non-overall fields

    if (editData && field === "overall") {
      setFormData((prev) => {
        const updated = { ...prev, overall: value };

        // Clear modules ONLY when overall is changed
        moduleKeys.forEach((k) => {
          updated[k] = "";
        });

        return updated;
      });

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    const testId = formData.exam_id || editData?.exam_id;
    const fields = testFields[testId] || [];

    if (!formData.exam_id) return showSnackbar("Please select an exam type");
    if (!formData.exam_date) return showSnackbar("Please select exam date");
    if (!formData.expiry_date) return showSnackbar("Please select expiry date");

    // Date validations
    if (dayjs(formData.exam_date).isAfter(dayjs())) {
      return showSnackbar("Exam date cannot be in the future");
    }
    if (dayjs(formData.expiry_date).isBefore(dayjs(), "day")) {
      return showSnackbar("Expiry date cannot be in the past");
    }

    // Score validations
    for (let field of fields) {
      const val = Number(formData[field.key]);

      if (formData[field.key] === undefined || formData[field.key] === "") {
        return showSnackbar(`${field.label} is required`);
      }

      if (isNaN(val) || val < field.min || val > field.max) {
        return showSnackbar(
          `${field.label} must be between ${field.min} and ${field.max}`
        );
      }
    }

    // clean payload
    const payload = {};
    ALLOWED_FIELDS.forEach((key) => {
      if (
        formData[key] !== undefined &&
        formData[key] !== null &&
        formData[key] !== ""
      ) {
        payload[key] = formData[key];
      }
    });

    // ✅ remap keys to match backend
    if (payload.exam_date) {
      payload.doe = payload.exam_date;
      delete payload.exam_date;
    }
    if (payload.expiry_date) {
      payload.doexpiry = payload.expiry_date;
      delete payload.expiry_date;
    }
    if (payload.overall !== undefined) {
      payload.over_all = payload.overall;
      delete payload.overall;
    }

    if (editData) {
      // in edit mode, don't send these fields
      delete payload.sp_user_id;
      delete payload.exam_id;
    } else {
      payload.test_id = formData.exam_id;
      delete payload.exam_id;
    }

    onSave(payload, editData?.id);
  };

  const testId = formData.exam_id || editData?.exam_id;
  const fields = testFields[testId] || [];
  const requiredLabel = (text) => (
    <span>
      {text} <span className="text-[#E81A1A] text-base">*</span>
    </span>
  );
  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle className="bg-[#572575] font-siromi text-xl font-medium text-white flex justify-between items-center">
          <span className="flex gap-2">
            <FilePenLine className="bg-[#FFFFFF26] p-1 rounded-md" />{" "}
            {editData
              ? `Edit Your ${
                  EXAMS.find((e) => e.id === testId)?.name
                } Information`
              : "Add Test Score"}
          </span>
          <IconButton onClick={onClose} className="text-white">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className="p-6">
          <Grid container spacing={3} className="mt-2">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>
                  Exam Type <span className="text-[#E81A1A] text-base">*</span>
                </InputLabel>
                <Select
                  value={formData.exam_id || ""}
                  disabled={!!editData}
                  onChange={(e) => handleChange("exam_id", e.target.value)}
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#9D0385",
                      fontWeight: "bold",
                    },
                  }}
                >
                  {availableExams.map((exam) => (
                    <MenuItem key={exam.id} value={exam.id}>
                      {exam.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label={requiredLabel("Date of Exam")}
                type="date"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={formData.exam_date || ""}
                onChange={(e) => handleChange("exam_date", e.target.value)}
                inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#9D0385",
                    fontWeight: "bold",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label={requiredLabel("Date of Expiry")}
                type="date"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={formData.expiry_date || ""}
                onChange={(e) => handleChange("expiry_date", e.target.value)}
                inputProps={{ min: dayjs().format("YYYY-MM-DD") }}
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#9D0385",
                    fontWeight: "bold",
                  },
                }}
              />
            </Grid>

            {fields.map(({ key, label, min, max }) => (
              <Grid item xs={12} md={4} key={key}>
                <TextField
                  label={
                    <span>
                      {label} ({min} - {max}){" "}
                      <span className="text-[#E81A1A] text-base">*</span>
                    </span>
                  }
                  type="number"
                  fullWidth
                  value={formData[key] ?? ""}
                  onChange={(e) => handleChange(key, e.target.value)}
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#9D0385",
                      fontWeight: "bold",
                    },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions className="p-6 pt-0">
          <Button
            variant="contained"
            onClick={handleSave}
            className="bg-[#572575] hover:bg-[#572575] font-altisunisa text-base text-white px-8 py-2 rounded-lg flex items-center gap-2 w-[50%] justify-center mx-auto"
          >
            <Check />{" "}
            {editData ? "Update Information" : "Yes, Save Information"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TestScoreModal;
